import { useState, useEffect } from 'react';
import { PageContainer } from '../../components/PageContainer';
import { Card, CardContent } from '@/components/ui/card';
import { StatCard } from '../../components/StatCard';
import { ConfidenceChart } from '../../components/ConfidenceChart';
import { RecentWords } from '../../components/RecentWords';
import { BookOpen, Trophy, Target } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';

function DashboardPage() {
  const [stats, setStats] = useState<{
    totalWords: number;
    learnedWords: number;
    learningWords: number;
    practiceSessions: number;
    fillInTheBlankSessions: number;
    newWords: number;
    wordsThisWeek: number;
    wordsThisMonth: number;
    wordsThisYear: number;
    confidenceDistribution: Array<{ level: number; count: number }>;
    avgConfidence: string;
    recentWords: Array<{
      word: string;
      definition: string;
      stars: number;
      addedAgo: string;
    }>;
  } | null>(null);

  // Helper function to format time ago
  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'today';
    if (diffInDays === 1) return '1 day ago';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return `${Math.floor(diffInDays / 30)} months ago`;
  };

  // Helper function to extract definition from payload
  const extractDefinition = (payload: unknown) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = payload as any;
      if (data?.data?.[0]?.meanings?.[0]?.definitions?.[0]?.definition) {
        return data.data[0].meanings[0].definitions[0].definition;
      }
      return 'Definition not available';
    } catch {
      return 'Definition not available';
    }
  };
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setError(null);
        const data = await dashboardService.getUserStats();

        // Transform API data to match component expectations
        const newWords = 0; // Placeholder since we removed learned functionality

        // Time-based stats are not provided by the API, so we set them to 0
        const wordsThisWeek = 0;
        const wordsThisMonth = 0;
        const wordsThisYear = 0;

        // Confidence level distribution from API
        const confidenceDistribution = [1, 2, 3, 4, 5].map((level) => ({
          level,
          count: data.ratingDistribution[level.toString() as keyof typeof data.ratingDistribution] || 0,
        }));

        // Average confidence level
        const totalRatings = Object.values(data.ratingDistribution).reduce((sum, count) => sum + count, 0);
        const weightedSum = Object.entries(data.ratingDistribution).reduce(
          (sum, [rating, count]) => sum + (parseInt(rating) * count), 0
        );
        const avgConfidence = totalRatings > 0 ? (weightedSum / totalRatings).toFixed(1) : '0';

        // Transform recent words from API data
        const recentWords = data.recentWords.slice(0, 4).map((item) => ({
          word: item.word,
          definition: extractDefinition(item.payload),
          stars: item.rating,
          addedAgo: formatTimeAgo(item.created_at),
        }));

        setStats({
          totalWords: data.totalWords,
          learnedWords: data.learnedWords,
          learningWords: data.learningWords,
          practiceSessions: data.practiceSessions || 0, // Mock data for now
          fillInTheBlankSessions: data.fillInTheBlankSessions || 0,
          newWords,
          wordsThisWeek,
          wordsThisMonth,
          wordsThisYear,
          confidenceDistribution,
          avgConfidence,
          recentWords,
        });
      } catch (err) {
        console.error('Failed to fetch dashboard stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      }
    };

    fetchDashboardStats();
  }, []);

  if (error) {
    return (
      <PageContainer>
        <div className="space-y-4 md:space-y-6">
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center">
                <p className="text-red-500 mb-2">Failed to load dashboard data</p>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    );
  }

  if (!stats) {
    return null;
  }

  // Transform confidence distribution data for the new component
  const confidenceLevels = stats.confidenceDistribution
    .sort((a, b) => b.level - a.level) // Sort in descending order (highest confidence first)
    .map((item) => {
      const percentage = stats.totalWords > 0 ? (item.count / stats.totalWords) * 100 : 0;

      // Define colors for different confidence levels (using grayscale hues)
      const colors = {
        1: 'bg-gray-200',
        2: 'bg-gray-300',
        3: 'bg-gray-500',
        4: 'bg-gray-700',
        5: 'bg-gray-900',
      };

      // Define star colors for different confidence levels (using grayscale hues)
      const starColors = {
        1: 'fill-gray-300 text-gray-300',
        2: 'fill-gray-400 text-gray-400',
        3: 'fill-gray-500 text-gray-500',
        4: 'fill-gray-600 text-gray-600',
        5: 'fill-gray-700 text-gray-700',
      };

      // Map numeric levels to descriptive confidence labels
      const confidenceLabels = {
        5: 'Mastered',
        4: 'Confident',
        3: 'Learning',
        2: 'Familiar',
        1: 'New',
      };

      return {
        stars: item.level,
        label: confidenceLabels[item.level as keyof typeof confidenceLabels] || `${item.level} stars`,
        percentage: Math.round(percentage),
        count: item.count,
        color: colors[item.level as keyof typeof colors] || 'bg-gray-500',
        starColor: starColors[item.level as keyof typeof starColors] || 'fill-muted text-muted',
      };
    });

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Stats Overview */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {/* Total Words */}
          <StatCard
            label="Total Vocabulary"
            value={stats.totalWords}
            description="words in your collection"
            icon={BookOpen}
          />

          {/* Words Learned */}
          <StatCard
            label="Words Mastered"
            value={stats.learnedWords}
            description="5-star confidence"
            icon={Trophy}
          />

          {/* Fill-in-the-Blank Sessions */}
          <StatCard
            label="Practice Sessions"
            value={stats.fillInTheBlankSessions}
            description="fill-in-the-blank completed"
            icon={Target}
          />
        </div>
        <ConfidenceChart confidenceLevels={confidenceLevels} />
        <RecentWords recentWords={stats.recentWords} />
      </div>
    </PageContainer>
  );
}

export default DashboardPage;
